import { LocalStorage, SessionStorage } from 'quasar';
import { clearAllObjectsFromIndex } from 'src/utils/indexedDb';
import { useAuthTokenStore } from 'src/store/main';
import { documentLockApiService } from 'src/services';
import { setApiRegion, clearPersistedApiRegion } from 'src/pages/api-base-url';
import { clearServiceWorkerCaches } from 'src/utils/cacheRecovery';
import { AUTH_URL } from 'src/pages/APIs';
import type { dynamicObj } from 'src/pages/assets';

// OAuth2 revocation endpoint (RFC 7009). Hardcoded path so that a broken
// SDK or import does not block logout — if anything in the stack here
// throws, the user must still be logged out.
const REVOKE_TOKEN_PATH = '/v2/oauth2/revoke';

// Auth-service logout endpoint. Blacklists the current access_token
// (Redis-backed) and clears the user's server-side cache. Distinct from
// revoke, which kills the refresh_token — together they fully invalidate
// the session server-side.
const LOGOUT_PATH = '/v2/auth/logout';

// Upper bound we wait for the blacklist call before redirecting: long enough
// to be observable (synthetic monitors assert the 2xx) and to actually land,
// short enough that a slow or hanging auth-service never traps the user on a
// half-torn-down page.
const LOGOUT_BLACKLIST_TIMEOUT_MS = 2000;

const TAB_CHANNEL_NAME = 'docbits-tab-channel';
export const tabChannel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(TAB_CHANNEL_NAME)
        : null;

// On `window` (not module scope) so duplicate module copies in the same tab
// share the flag — handleCrossTabMessage uses it to drop self-sent LOGOUT.
declare global {
    interface Window {
        __docbits_logging_out?: boolean;
    }
}

/**
 * Tell the auth-service to invalidate this session's refresh_token before
 * we tear down local storage and redirect.
 *
 * Best-effort by design:
 *   - We do NOT await. A slow or failing /oauth2/revoke must never delay
 *     or block logout — the user clicked "logout" and expects to be out.
 *   - `keepalive: true` lets the browser finish the POST after the
 *     subsequent `window.location.href = '/'` unloads the page.
 *   - All errors are swallowed: when this function returns, logout
 *     proceeds regardless of revocation success. Worst case, the
 *     refresh_token stays valid on the backend until its natural expiry
 *     (30 days) — same as before this revoke call existed.
 *
 * Why we still bother: stops a stolen/leaked refresh_token from being
 * usable after the legitimate user logged out (the most common
 * post-incident credential-rotation gap).
 */
function revokeRefreshTokenBestEffort(): void {
    try {
        const tokenDetails = LocalStorage.getItem('token_details') as {
            refresh_token?: string;
        } | null;
        const refreshToken = tokenDetails?.refresh_token;
        if (!refreshToken) {
            return;
        }
        const body = new URLSearchParams({
            token: refreshToken,
            token_type_hint: 'refresh_token',
        });
        void fetch(AUTH_URL + REVOKE_TOKEN_PATH, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: body.toString(),
            keepalive: true,
            credentials: 'omit',
        }).catch(() => {
            // Swallow: best-effort revoke. Server may be slow or down;
            // refresh_token will then simply rely on its natural expiry.
        });
    } catch {
        // Storage corruption, type mismatch, etc. must never block logout.
    }
}

/**
 * Blacklist the current access_token at the auth-service so it cannot be
 * replayed after logout, and clear the user's server-side cache.
 *
 * Unlike revoke (refresh_token, fire-and-forget) this call is *awaited* with
 * a short timeout:
 *   - awaited so the token is actually invalidated before we hand back
 *     control, and so synthetic monitors can observe the 2xx.
 *   - the AbortController caps the wait at LOGOUT_BLACKLIST_TIMEOUT_MS — a
 *     slow/hanging auth-service must never trap the user mid-logout.
 *   - every error (network, abort, storage) is swallowed: logout proceeds
 *     regardless. Worst case the access_token stays valid until its natural
 *     expiry — same as before this call existed.
 */
async function blacklistAccessToken(): Promise<void> {
    let accessToken: string | undefined;
    try {
        const tokenDetails = LocalStorage.getItem('token_details') as {
            access_token?: string;
        } | null;
        accessToken = tokenDetails?.access_token;
    } catch {
        // Storage corruption etc. must never block logout.
        return;
    }
    if (!accessToken) {
        return;
    }
    const controller = new AbortController();
    const timer = setTimeout(
        () => controller.abort(),
        LOGOUT_BLACKLIST_TIMEOUT_MS,
    );
    try {
        await fetch(AUTH_URL + LOGOUT_PATH, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
            credentials: 'omit',
        });
    } catch {
        // Network error / timeout / abort: best-effort, logout proceeds.
    } finally {
        clearTimeout(timer);
    }
}

// Keys to preserve on logout (user preferences & app state)
// Keep these as literals instead of reading CONSTANTS at module load time.
// LogoutUtil is pulled into early auth/cache chunks; top-level CONSTANTS access
// can create a production bundle cycle and prevent Vue from mounting.
const KEEP_ON_LOGOUT = new Set([
    'selected_language',
    'remembered_email',
    'auth_region',
    'APP_VERSION',
    'versionRefreshCooldownUntil',
    'tasks_list_mode',
    'fullSearchFilters',
    'group_parent_indexes',
    'group_parent_raw_where',
    'IMPORT_LOG_SETTING_COLUMNS',
    'EXPORT_SETTING_COLUMNS_ORDER',
    'USERS_SETTING_COLUMNS_ORDER',
    'GROUPS_COLUMNS_ORDER',
    'GROUPS_SORT_ORDER',
    'SUB_ORG_COLUMNS_ORDER',
    'EMAIL_TEMPLATE_SETTING_COLUMNS_ORDER',
    'EMAIL_NOTIFICATION_SETTING_COLUMNS_ORDER',
    'SUPPLIER_EMAIL_TEMPLATE_SETTING_COLUMNS_ORDER',
    'CUSTOM_COLUMNS_ORDER',
    'CLASSIFICATION_EXTRACTION_SETTING_COLUMNS',
    'TASKS_COLUMNS_SETTING',
    'TASKS_LIST_SETTINGS',
    'workflow_dashboard_filters',
]);

// Substrings of dynamic user-based keys to preserve (e.g. {userId}_dashboard_filters_v2)
const KEEP_PATTERNS_ON_LOGOUT = [
    'dashboard_filters_v2',
    'dashboard_groups',
    'dashboard_pagination',
    'columnWidths_',
    'columnFilter_',
    'columnResize',
    'orderBy_',
    'Log-Settings',
    'emailImport',
    'ftpImport',
    'listOfValues',
    'script_logs_setting',
    'masterDataTabs',
    'lov_custom_order_',
];

function shouldKeepOnLogout(key: string): boolean {
    if (KEEP_ON_LOGOUT.has(key)) return true;
    return KEEP_PATTERNS_ON_LOGOUT.some((pattern) => key.includes(pattern));
}

// Expire every cookie that document.cookie exposes at logout time.
//
// A cookie delete only lands if the expiry matches the cookie's exact
// path+domain pair, so for each enumerated cookie we re-issue the expiry
// across every combination it could have been set with:
//   - path: '/' plus each cumulative prefix of the current pathname
//     (e.g. on '/a/b' → '/', '/a', '/a/b').
//   - domain: unset, the host, and the dot-host (host-only + domain-wide).
//
// LIMITATIONS (inherent to document.cookie — none are auth-relevant):
//   - HttpOnly cookies are invisible to JS. The real session is HttpOnly on
//     the auth-service domain (auth.docbits.com) and is killed server-side by
//     the revoke + blacklist calls, NOT here.
//   - document.cookie only enumerates cookies whose path matches the document
//     that originally loaded the SPA (typically '/'), so a cookie scoped to a
//     deeper non-root path may not appear here and would survive. The app sets
//     its cookies at the root path, so this is a theoretical gap only. No
//     client-side API can clear a cookie on a path the current document does
//     not match — that is a browser boundary, not a code defect.
// Net effect: removes the non-HttpOnly, root-path cookies the app origin
// accumulated (analytics, feature flags, etc.) so no such cookie survives logout.
function clearBrowserCookies() {
    if (typeof document === 'undefined' || !document.cookie) {
        return;
    }
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    const host = window.location.hostname;

    // Cumulative path prefixes of the current pathname, root first.
    const paths = ['/'];
    let acc = '';
    for (const segment of window.location.pathname.split('/').filter(Boolean)) {
        acc += '/' + segment;
        paths.push(acc);
    }

    document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0].trim();
        if (!name) return;
        for (const path of paths) {
            document.cookie = `${name}=;${expiry};path=${path}`;
            document.cookie = `${name}=;${expiry};path=${path};domain=${host}`;
            document.cookie = `${name}=;${expiry};path=${path};domain=.${host}`;
        }
    });
}

function clearStorageForLogout() {
    const auth_token_store = useAuthTokenStore();
    auth_token_store.$reset();

    // Clear all localStorage keys except the ones we want to keep
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !shouldKeepOnLogout(key)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach((key) => LocalStorage.remove(key));

    clearAllObjectsFromIndex();
    SessionStorage.clear();
    clearBrowserCookies();
    setApiRegion('eu');
    clearPersistedApiRegion();
}

function unlockDoc(doc_id: any, doc_lock: any) {
    const params = {
        doc_id: doc_id,
        lock_key: doc_lock,
        force: 'true',
    };
    documentLockApiService.release(params);
}

async function logout() {
    // Defensive calls without a token (e.g. redirectToLogin during boot on /)
    // must not broadcast/navigate — would cascade-reload sibling tabs.
    const hadSession = !!LocalStorage.getItem('token_details');

    const doc_info = SessionStorage.getItem('doc_info') as dynamicObj;
    const lock_key = SessionStorage.getItem('doc_lock_key') as string;
    if (doc_info && lock_key) {
        unlockDoc(doc_info.doc_id, lock_key);
    }

    if (hadSession) {
        // Set BEFORE postMessage so a same-tab duplicate channel sees it.
        window.__docbits_logging_out = true;
        if (tabChannel) {
            try {
                tabChannel.postMessage({ type: 'LOGOUT' });
            } catch (err) {
                console.error('[LogoutUtil] failed to broadcast LOGOUT:', err);
            }
        }
        // Invalidate server-side BEFORE clearing storage — tokens needed.
        // revoke kills refresh_token (fire-and-forget); blacklist kills
        // access_token (awaited so it actually lands).
        revokeRefreshTokenBestEffort();
        await blacklistAccessToken();
    }

    // Always wipe storage — guarantees no leaked Pinia/IndexedDB/Session state.
    clearStorageForLogout();

    if (hadSession) {
        // Drop the service-worker cache so the next session never gets a stale
        // cached response. Awaited so deletion lands before the navigation
        // cancels it. Gated on hadSession: a defensive boot-time logout
        // (redirectToLogin on `/`) must NOT nuke the precache on every load.
        await clearServiceWorkerCaches();
        window.location.href = '/';
    }
}

export {
    logout,
    clearStorageForLogout,
    shouldKeepOnLogout,
    revokeRefreshTokenBestEffort,
    blacklistAccessToken,
    KEEP_ON_LOGOUT,
    KEEP_PATTERNS_ON_LOGOUT,
    REVOKE_TOKEN_PATH,
    LOGOUT_PATH,
};

export default {
    methods: {
        logout,
        clearStorageForLogout,
    },
};
